/**
 * The location grid (brief §6).
 *
 * Pure and free of `node:` imports, so the same code decides cells on the server, in the
 * browser and inside the APK. `h3-js` is one of the four runtime dependencies and is pure
 * JavaScript plus WebAssembly, so it bundles for all three without a native build.
 *
 * The rule this module exists to enforce: **a reported position is turned into a cell and then
 * discarded.** Nothing here returns a caller's coordinate back to them, and the only point it
 * hands out is the centre of a cell.
 */

import {
  cellToBoundary,
  cellToChildren,
  cellToLatLng,
  cellToParent,
  getHexagonEdgeLengthAvg,
  getResolution,
  gridDisk,
  isValidCell,
  latLngToCell,
  UNITS,
} from 'h3-js';

/**
 * Storage and reporting resolution. Edge ≈ 174 m, area ≈ 0.105 km².
 *
 * Coarse enough that a report cannot point at one person's front door, fine enough that
 * Marktplatz and Hauptbahnhof in Stuttgart are different cells. Changing it invalidates every
 * stored cell, so it is a constant and not a setting.
 */
export const REPORT_RESOLUTION = 9;

/** Display resolution below zoom 12. Edge ≈ 1.22 km, area ≈ 5.16 km². */
export const OVERVIEW_RESOLUTION = 7;

/** Region resolution: the granularity a client asks for hot cells at (edge ≈ 8.5 km). */
export const REGION_RESOLUTION = 5;

/**
 * Reports with a worse GPS accuracy than this are refused.
 *
 * At 500 m of scatter the assigned cell is a guess, and a guessed cell does not merely add
 * noise — it moves density to a place nobody was.
 */
export const MAX_ACCURACY_METRES = 100;

export type Cell = string;

export function cellFor(lat: number, lon: number, resolution = REPORT_RESOLUTION): Cell {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new RangeError('lat and lon must be finite numbers');
  }
  if (lat < -90 || lat > 90) throw new RangeError('lat must be between -90 and 90');
  if (lon < -180 || lon > 180) throw new RangeError('lon must be between -180 and 180');
  return latLngToCell(lat, lon, resolution);
}

/** The centre of a cell — the only coordinate this application ever stores or returns. */
export function centreOf(cell: Cell): { lat: number; lon: number } {
  const [lat, lon] = cellToLatLng(cell);
  return { lat, lon };
}

/** The cell's outline, as GeoJSON ring coordinates (`[lon, lat]`, closed). */
export function boundaryOf(cell: Cell): [number, number][] {
  const ring = cellToBoundary(cell, true) as [number, number][];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first !== undefined && last !== undefined && (first[0] !== last[0] || first[1] !== last[1])) {
    return [...ring, first];
  }
  return ring;
}

/**
 * The cell and its neighbours out to `k` rings.
 *
 * `k = 1` is seven cells and is the "near enough" radius used by the confirmation and
 * pass-by rules — about 350 m across, which is a short walk rather than a sighting line.
 */
export function neighbours(cell: Cell, k = 1): Cell[] {
  return gridDisk(cell, k);
}

export function isNear(a: Cell, b: Cell, k = 1): boolean {
  return a === b || neighbours(b, k).includes(a);
}

export function parentOf(cell: Cell, resolution = OVERVIEW_RESOLUTION): Cell {
  return cellToParent(cell, resolution);
}

/** The cells of a finer resolution inside `cell` — a district's 49 report cells, say. */
export function childrenOf(cell: Cell, resolution = REPORT_RESOLUTION): Cell[] {
  return cellToChildren(cell, resolution);
}

/** The res-5 region a cell belongs to — the only granularity the geofence API accepts. */
export function regionOf(cell: Cell): Cell {
  return cellToParent(cell, REGION_RESOLUTION);
}

export function resolutionOf(cell: Cell): number {
  return getResolution(cell);
}

/**
 * Is this a well-formed H3 index at all?
 *
 * `isValidCell` rather than a regular expression: a fifteen-character hex string is not the
 * same thing as a cell, and handing an invalid one to `cellToLatLng` throws from inside the
 * WebAssembly module with a message that names neither the input nor the caller.
 */
export function isCell(value: unknown): value is Cell {
  return typeof value === 'string' && isValidCell(value);
}

/**
 * The database stores a cell as `bigint`, because that is what an H3 index is; `h3-js` speaks
 * the 15-character hex form.
 *
 * These conversions go through `BigInt` and **never** through `Number`. An H3 index needs all
 * 64 bits and JavaScript numbers carry 53 — `Number('8928308280fffff')` silently returns a
 * different cell, and the map would show density in the wrong place with nothing to indicate
 * anything had gone wrong. `pg` hands `bigint` back as a string for the same reason.
 */
export function cellToDb(cell: Cell): string {
  return BigInt(`0x${cell}`).toString(10);
}

export function cellFromDb(value: string | bigint): Cell {
  return BigInt(value).toString(16);
}

/**
 * How far outside a bounding box a cell's **centre** can lie while the cell still shows in it.
 *
 * Cells are stored as a centre point, so the obvious query — "centre inside the box" — quietly
 * means "only cells whose middle you can see". A hexagon is not a point: at res 9 it is about
 * 400 m across, so once you zoom past roughly z16 the cell you are standing in is wider than
 * the screen, its centre is off it, and the map goes empty while you are inside a hotspot.
 *
 * Twice the average edge length. Once would be the circumradius and enough for the res-9 case;
 * the second is for the overview, where res-9 rows are rolled up into their res-7 parents and
 * a parent overlapping the edge needs *all* its children, not the ones that happen to fall
 * inside. Over-fetching a ring of cells is cheap and invisible; under-fetching is the bug.
 */
export function cellPadMetres(resolution: number): number {
  return getHexagonEdgeLengthAvg(resolution, UNITS.m) * 2;
}
